/**
 * The three states the panel knows about.
 *
 * The backend speaks three different vocabularies depending on which task is
 * talking — see `RAW_STATUS_MAP` — so nothing downstream of the reducer ever
 * touches a raw status.
 */
export const NOTIFICATION_STATUS = {
  RUNNING: 'running',
  SUCCESS: 'success',
  ERROR: 'error',
  /**
   * Not a status the backend ever sends: it is what a running entry becomes
   * when it comes back from disk. Its closing event went out while the tab was
   * closed and is never coming back, so the row says so instead of spinning
   * forever. See `notificationsPersist`.
   */
  INTERRUPTED: 'interrupted',
  /** Finished, but some of it failed: a success with a `failed` count. */
  PARTIAL: 'partial',
  /** Running, but nothing has been heard for longer than its kind allows. */
  STALLED: 'stalled',
  /** The request that starts it got no answer, so it may or may not be running. */
  UNCONFIRMED: 'unconfirmed',
};

/** States that are over: the entry no longer changes on its own. */
export const FINISHED_STATUSES = [
  NOTIFICATION_STATUS.SUCCESS,
  NOTIFICATION_STATUS.ERROR,
  NOTIFICATION_STATUS.INTERRUPTED,
  NOTIFICATION_STATUS.PARTIAL,
];

/**
 * States a late event from the backend can still close. A stalled job may be
 * slow rather than dead, and an unconfirmed one may have started anyway.
 */
export const OPEN_STATUSES = [
  NOTIFICATION_STATUS.RUNNING,
  NOTIFICATION_STATUS.STALLED,
  NOTIFICATION_STATUS.UNCONFIRMED,
];

/**
 * Every word the four producers can put in `status`, flattened onto the three
 * above.
 *
 * They genuinely disagree: the deliveries export says `started`/`completed`,
 * the zone coverage says `init`/`success`, and only two of them ever report a
 * failure. Reading `status` directly is why the legacy shows an empty toast
 * when an export fails — it switches on `"failed"`, a string the backend has
 * never sent.
 */
export const RAW_STATUS_MAP = {
  started: NOTIFICATION_STATUS.RUNNING,
  processing: NOTIFICATION_STATUS.RUNNING,
  init: NOTIFICATION_STATUS.RUNNING,
  completed: NOTIFICATION_STATUS.SUCCESS,
  success: NOTIFICATION_STATUS.SUCCESS,
  error: NOTIFICATION_STATUS.ERROR,
  failed: NOTIFICATION_STATUS.ERROR,
};

/**
 * Channel prefixes. Each one is a Celery task on the other side, and the value
 * is the literal prefix the backend builds its Redis channel with.
 */
export const NOTIFICATION_KIND = {
  DOWNLOAD_DELIVERIES: 'download',
  DOWNLOAD_HISTORY: 'download_history',
  DOWNLOAD_SELLERS: 'download_sellers',
  ZONE_COVERAGE: 'shipping_zone_coverage',
  ORDER_UPLOAD: 'order_upload',
  /** The courier answered a label request from the warehouse. */
  LABEL: 'label',
};

/**
 * What each kind is called and what to say about it when the payload says
 * nothing.
 *
 * Only the zone coverage sends a `message` of its own, and it is a good one
 * (it names the zone and counts the communes), so it wins whenever it is
 * there. The exports send a bare status, and these are the sentences that fill
 * the gap.
 */
export const NOTIFICATION_KINDS = {
  [NOTIFICATION_KIND.DOWNLOAD_DELIVERIES]: {
    title: 'Exportar deliveries',
    running: 'Generando el reporte de envíos...',
    success: 'Reporte listo. También te lo enviamos por correo.',
    error: 'No se pudo generar el reporte de envíos.',
  },
  [NOTIFICATION_KIND.DOWNLOAD_HISTORY]: {
    title: 'Historial de estados',
    running: 'Generando el historial de estados...',
    success: 'Historial listo. Te lo enviamos por correo.',
    error: 'No se pudo generar el historial de estados.',
  },
  [NOTIFICATION_KIND.DOWNLOAD_SELLERS]: {
    title: 'Reporte de sellers',
    running: 'Generando el reporte de sellers...',
    success: 'Reporte listo. Te lo enviamos por correo.',
    error: 'No se pudo generar el reporte de sellers.',
  },
  [NOTIFICATION_KIND.ZONE_COVERAGE]: {
    title: 'Cobertura de la zona',
    running: 'Procesando la cobertura...',
    success: 'Cobertura procesada.',
    error: 'No se pudo procesar la cobertura.',
  },
  /*
   * The only one of these whose `success` may still carry bad news: an upload
   * that created most of its orders and failed on a few reports as a success,
   * because a status outside `RAW_STATUS_MAP` would leave the row spinning.
   * The task always sends a message saying how many of each, and these
   * sentences are only the fallback.
   */
  /*
   * The Preparar screen opens a dialog with this answer the moment it lands,
   * so the panel entry is only for whoever walked away before the courier
   * replied. No start toast for the same reason.
   */
  [NOTIFICATION_KIND.LABEL]: {
    title: 'Etiqueta del courier',
    running: 'Creando el envío en el courier...',
    success: 'Envío creado en el courier.',
    error: 'El courier rechazó el envío.',
    startToast: false,
  },
  [NOTIFICATION_KIND.ORDER_UPLOAD]: {
    title: 'Carga masiva de órdenes',
    running: 'Creando las órdenes del archivo...',
    success: 'Carga terminada.',
    error: 'No se pudo procesar la carga.',
    // The upload screen is its own progress panel: a toast on the click would
    // repeat what the user is looking at.
    startToast: false,
  },
};

/** What an entry says in the states the backend never names. */
export const STATUS_MESSAGES = {
  [NOTIFICATION_STATUS.PARTIAL]: 'Terminó, pero una parte no se pudo procesar.',
  [NOTIFICATION_STATUS.STALLED]:
    'Sin novedades del proceso. Puede que siga corriendo: revisa antes de volver a pedirlo.',
  [NOTIFICATION_STATUS.UNCONFIRMED]:
    'No tuvimos respuesta del servidor. Puede que haya empezado igual: espera unos minutos antes de volver a pedirlo.',
};

/**
 * How long a running entry may go without news before it reads as stalled.
 * The exports and the coverage report every few seconds once they start; the
 * slack is for a queue that is busy with someone else's job.
 */
export const STALLED_AFTER_MS = 15 * 60 * 1000;

/**
 * Prefixes that ride the same socket but are not news for the panel.
 *
 * The reducer keeps unknown prefixes on purpose, so that a task added on the
 * backend shows up looking odd rather than vanishing. That is the right default
 * for a job the user started — and the wrong one for a signal, which is what
 * `dashboard` is: it fires on every status change, and without this every
 * shipment that moved would open a row nobody asked for. The dashboard slice
 * picks it up from the same action.
 */
export const SILENT_NOTIFICATION_KINDS = ['dashboard'];

/** How many entries the panel keeps before dropping the oldest. */
export const MAX_NOTIFICATIONS = 30;

/** What a restored entry says once its job can no longer report back. */
export const INTERRUPTED_MESSAGE = 'Se interrumpió mientras no estabas; vuelve a pedirlo.';

/**
 * How long a finished entry keeps its download button.
 *
 * The only link any of these jobs sends is a presigned S3 URL, and it lives for
 * seven days — see `describeEvent`. Past that the entry is still worth showing;
 * the button behind it is not, because it answers a 403.
 */
export const DOWNLOAD_LINK_TTL_MS = 7 * 24 * 60 * 60 * 1000;
